import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Alert, Badge, Card, Select, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can } from '../../lib/permissions';
import { ProjectsNav } from './ProjectsNav';
import { formatDate, priorityName, type RequestListItem, type RequestScope, statusName, statusTone } from './requests-data';

const FILTERS = ['abiertas', 'aprobada', 'rechazada', 'retirada', 'todas'] as const;

/** Solicitudes de proyecto (guía 7.2): las propias, las que esperan mi decisión y, para IT, todas. */
export function RequestsPage() {
  const me = useMe();
  const staff = can(me, 'proyectos.ver') || can(me, 'proyectos.viabilidad') || can(me, 'proyectos.aprobar');
  const scopes: RequestScope[] = ['propias', 'por_aprobar', ...(staff ? (['todas'] as const) : [])];
  const [params, setParams] = useSearchParams();
  const requested = params.get('alcance') as RequestScope | null;
  const scope: RequestScope = requested && scopes.includes(requested) ? requested : 'propias';
  const filter = (FILTERS as readonly string[]).includes(params.get('estado') ?? '') ? params.get('estado')! : 'abiertas';

  const query = new URLSearchParams({ alcance: scope });
  if (filter !== 'todas') query.set('estado', filter);
  const requests = useQuery({
    queryKey: ['solicitudes', 'lista', scope, filter],
    queryFn: () => api<RequestListItem[]>(`/api/proyectos/solicitudes?${query}`),
    placeholderData: (prev) => prev,
  });
  // Cuántas esperan mi decisión: se muestra en la pestaña aunque esté en otra.
  const pending = useQuery({
    queryKey: ['solicitudes', 'lista', 'por_aprobar', 'abiertas'],
    queryFn: () => api<RequestListItem[]>('/api/proyectos/solicitudes?alcance=por_aprobar&estado=abiertas'),
  });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('sp.titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('sp.descripcion')}</p>
        </div>
        <Link to="/proyectos/solicitudes/nueva" className="rounded bg-marca px-4 py-2 text-sm font-medium text-white hover:bg-marca-claro">
          {t('sp.nueva')}
        </Link>
      </div>

      <ProjectsNav />

      <div role="tablist" aria-label={t('sp.alcance')} className="flex gap-1 border-b border-slate-200">
        {scopes.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={s === scope}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${s === scope ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            onClick={() => update({ alcance: s })}
          >
            {t(`sp.alcance_${s}` as MessageKey)}
            {s === 'por_aprobar' && !!pending.data?.length && (
              <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-900">{pending.data.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="w-56">
        <Select id="sp-estado" label={t('sp.estado')} value={filter} onChange={(e) => update({ estado: e.target.value === 'abiertas' ? null : e.target.value })}>
          {FILTERS.map((f) => (
            <option key={f} value={f}>
              {t(`sp.filtro_${f}` as MessageKey)}
            </option>
          ))}
        </Select>
      </div>

      <Card className="p-0">
        {!requests.data ? (
          <div className="p-5">{requests.error ? <Alert>{errorMessage(requests.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
        ) : requests.data.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{scope === 'por_aprobar' ? t('sp.vacio_por_aprobar') : t('sp.vacio')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('sp.col_solicitud')}</th>
                  <th className="px-4 py-2 font-medium">{t('sp.col_etapa')}</th>
                  <th className="px-4 py-2 font-medium">{t('sp.col_solicitante')}</th>
                  <th className="px-4 py-2 font-medium">{t('sp.col_prioridad')}</th>
                  <th className="px-4 py-2 font-medium">{t('sp.col_fecha')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.data.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/proyectos/solicitudes/${r.number}`} className="font-medium text-slate-900 hover:underline">
                        <span className="mr-2 font-mono text-xs text-slate-500">{r.code}</span>
                        {r.title}
                      </Link>
                      <span className="block text-xs text-slate-500">{r.organization}</span>
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={statusTone(r.status)}>{statusName(r.status)}</Badge>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{r.requester ?? '—'}</td>
                    {/* La prioridad interna solo llega para el jefe directo e IT; al solicitante se le muestra su urgencia. */}
                    <td className="px-4 py-2 text-slate-700">
                      {r.priority ? priorityName(r.priority) : <span className="text-slate-500">{t('sp.urgencia_corta', { urgencia: priorityName(r.perceivedUrgency) })}</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
